import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag } from 'antd';
import { KeyOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import moment from 'moment';

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [recentActivations, setRecentActivations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, activationsRes] = await Promise.all([
        axios.get('https://keyapi.dva.ink/api/admin/stats', { headers }),
        axios.get('https://keyapi.dva.ink/api/admin/activations?limit=10', { headers })
      ]);

      setStats(statsRes.data);
      setRecentActivations(activationsRes.data.activations);
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const pieData = [
    { name: '已使用', value: stats.usedKeys || 0, color: '#52c41a' },
    { name: '未使用', value: (stats.totalKeys || 0) - (stats.usedKeys || 0), color: '#1890ff' }
  ];

  const columns = [
    {
      title: '卡密',
      dataIndex: 'card_key',
      key: 'card_key',
      render: (text) => <code>{text}</code>
    },
    {
      title: '设备名称',
      dataIndex: 'device_name',
      key: 'device_name',
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: '激活时间',
      dataIndex: 'activated_at',
      key: 'activated_at',
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => {
        const isExpired = moment().isAfter(record.expires_at);
        return (
          <Tag color={isExpired ? 'red' : 'green'}>
            {isExpired ? '已过期' : '正常'}
          </Tag>
        );
      }
    }
  ];

  return (
    <div>
      <h2>仪表盘</h2>
      
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总卡密数"
              value={stats.totalKeys || 0}
              prefix={<KeyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已使用卡密"
              value={stats.usedKeys || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃设备"
              value={stats.activeDevices || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="过期设备"
              value={stats.expiredDevices || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="卡密使用情况">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="设备状态统计">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: '活跃设备', value: stats.activeDevices || 0 },
                { name: '过期设备', value: stats.expiredDevices || 0 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Card title="最近激活记录" loading={loading}>
        <Table
          columns={columns}
          dataSource={recentActivations}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Dashboard;
